import { fmt, pct, type CalcResult } from "@h2lca/core";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/FormControls";
import { InfoTip } from "@/components/ui/InfoTip";
import { css, STAGE_COLOR } from "@/lib/design";
import { RESULT_SHARE_MIN_VISIBLE_PERCENT } from "@/lib/domain-rules";
import { useWorkshop } from "@/store/workshop";
import { StatusBadge } from "./Badges";
import { DENOMINATOR_LABEL } from "./helpers";

/** 오른쪽 결과 판 , 입력이 바뀌면 바로 갱신되는 대표 수치,기여도,인증 등급,검증 상태 */
export function ResultPanel({ result, compact = false }: { result: CalcResult; compact?: boolean }) {
  const setStep = useWorkshop((s) => s.setStep);
  const select = useWorkshop((s) => s.select);
  const nav = useNavigate();
  const errors = result.issues.filter((i) => i.level === "error");
  const warnings = result.issues.filter((i) => i.level === "warning");
  const top = result.topEquipment.slice(0, 3);
  const stageOf = (id: string) => result.byEquipment.find((e) => e.equipmentId === id)?.stage ?? "auxiliary";

  return (
    <div className="flex flex-col gap-content rounded-panel border border-line-soft bg-panel p-content">
      <div className="flex items-center justify-between gap-item">
        <h2 className="text-subhead font-bold text-ink">산정 결과</h2>
        <StatusBadge status={result.status} />
      </div>

      <div className="result-hero">
        <p className="text-micro font-semibold">1 kg당 배출량</p>
        <p className="mt-label">
          <span className="result-hero-value">{result.perKgH2 === null ? "없음" : fmt(result.perKgH2, 3)}</span>
          <span className="result-hero-unit">kgCO2eq/kgH2</span>
        </p>
        <div className="result-hero-sub">
          <span>기능단위 수소 {fmt(result.functionalUnitKg)} kg의 배출량 <b className="font-mono">{fmt(result.perFunctionalUnitKgCO2eq)}</b> kgCO2eq/FU</span>
          <span>
            총 배출량 <b className="font-mono">{fmt(result.totalKgCO2eq)}</b> kgCO2eq
          </span>
          <span>
            분모 {DENOMINATOR_LABEL[result.denominator.kind]} <b className="font-mono">{fmt(result.denominator.kg)}</b> kg
          </span>
        </div>
        {result.status === "blocked" && <p className="mt-item text-micro">필수값 누락 또는 오류가 있어 최종 결과를 산출하지 않습니다.</p>}
      </div>

      {!compact && (
        <div>
          <p className="mb-inline flex items-center gap-label text-caption font-semibold text-ink">
            배출량이 큰 설비는?
            <InfoTip label="기여도 설명">시스템 경계 안 설비의 배출량을 큰 순서로 셋만 보입니다. 비율은 총 배출량 대비입니다.</InfoTip>
          </p>
          {top.length === 0 ? (
            <p className="text-footnote text-ink-subtle">아직 배출량이 계산된 설비가 없습니다.</p>
          ) : (
            <ol className="flex flex-col gap-inline">
              {top.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="flex w-full flex-col gap-label rounded-control px-label py-detail text-left transition-colors hover:bg-surface-sunken"
                    onClick={() => {
                      select(c.equipmentId);
                      setStep("inputs");
                    }}
                    title="운전자료 입력으로 이동"
                  >
                    <span className="flex items-baseline justify-between gap-item text-caption">
                      <span className="truncate text-ink">{c.label}</span>
                      <span className="shrink-0 font-mono text-ink-muted">
                        {fmt(c.kgCO2eq)} <span className="text-micro">kg</span>, {pct(c.share)}
                      </span>
                    </span>
                    <span className="share-track">
                      <span className="share-fill block" style={{ width: `${Math.max(RESULT_SHARE_MIN_VISIBLE_PERCENT, c.share * 100)}%`, backgroundColor: css(STAGE_COLOR[stageOf(c.equipmentId)].fill) }} />
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {result.cert && (
        <div className={`note ${result.cert.eligible && result.cert.grade !== null ? "note-ok" : "note-warn"} text-caption`}>
          {result.cert.eligible ? (
            result.cert.grade !== null ? (
              <span>
                청정수소 인증제 모의 등급 <b className="font-mono">{result.cert.grade}등급</b>
                {result.cert.thresholdKgCO2eq !== null && <span className="text-micro"> (기준 {result.cert.thresholdKgCO2eq} kgCO2eq/kgH2 이하)</span>}
              </span>
            ) : (
              <span>{result.perKgH2 === null ? "결과가 산출되면 등급을 판정합니다" : `청정수소 기준(${result.cert.thresholdKgCO2eq ?? "없음"} kgCO2eq/kgH2) 초과. 등급 없음`}</span>
            )
          ) : (
            <span>인증제 모의산정 조건 미충족: {result.cert.reasons[0]}</span>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-inline text-footnote">
        {errors.length > 0 ? (
          <Button size="compact" variant="danger" onClick={() => setStep("calc")}>
            오류 {errors.length} 확인
          </Button>
        ) : (
          <span className="badge-soft badge-ok">오류 없음</span>
        )}
        {warnings.length > 0 && (
          <Button size="compact" onClick={() => setStep("calc")}>
            경고 {warnings.length} 확인
          </Button>
        )}
        <span className="text-ink-subtle">{result.methodLabel}</span>
      </div>

      {!compact && (
        <div className="flex gap-item">
          <Button variant="secondary" fullWidth onClick={() => setStep("result")}>
            결과 자세히
          </Button>
          <Button variant="secondary" fullWidth onClick={() => nav("/app/report")}>
            보고서
          </Button>
        </div>
      )}
    </div>
  );
}
